import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { signInWithEmailAndPassword, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../config/firebase';
import Form from './Form';
import Input from './Input';
import Button from './Button';
import Alert from './Alert';

interface FinalStepProps {
  userId?: string;
}

const FinalStep: React.FC<FinalStepProps> = ({ userId }) => {
  const { loading, error, clearError } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [localLoading, setLocalLoading] = useState(false);
  const [userData, setUserData] = useState<{
    email: string;
    userType?: 'startup' | 'investor';
  } | null>(null);
  const [loadingUserData, setLoadingUserData] = useState(false);

  // Clear errors when user starts typing
  const [hasTyped, setHasTyped] = useState(false);

  React.useEffect(() => {
    if (hasTyped && (email || password)) {
      clearError();
      setLocalError('');
    }
  }, [email, password, hasTyped, clearError]);

  // Fetch user data when userId is provided
  React.useEffect(() => {
    if (!userId) {
      setLocalError('User ID is required');
      return;
    }

    const fetchUserData = async () => {
      try {
        setLoadingUserData(true);
        const getUser = httpsCallable(functions, 'getUser');

        const result = await getUser({ userId });
        const data = result.data as {
          success: boolean;
          user?: {
            email: string;
            userType?: 'startup' | 'investor';
            emailVerified?: boolean;
            isPreview?: boolean;
          };
          error?: string;
        };

        if (data.success && data.user) {
          // Password creation only after email verify — block /finalstep deep links.
          if (data.user.isPreview === true && data.user.emailVerified !== true) {
            navigate(`/verify-email?userId=${encodeURIComponent(userId)}`, { replace: true });
            return;
          }
          setEmail(data.user.email || '');
          setUserData({
            email: data.user.email || '',
            userType: data.user.userType,
          });
        } else {
          setLocalError(data.error || 'Failed to load user data');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setLocalError('Failed to load user data');
      } finally {
        setLoadingUserData(false);
      }
    };

    fetchUserData();
  }, [userId, navigate]);

  // Redirect if no user is logged in
  //   React.useEffect(() => {
  //     if (!firebaseUser) {
  //       navigate('/login');
  //     }
  //   }, [firebaseUser, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError('');
    setHasTyped(false);

    // Basic validation
    if (!email) {
      setLocalError('Email is required');
      return;
    }

    if (!email.includes('@')) {
      setLocalError('Please enter a valid email address');
      return;
    }

    if (!password) {
      setLocalError('Password is required');
      return;
    }

    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters long');
      return;
    }

    if (!/\d/.test(password)) {
      setLocalError('Password must contain at least one number');
      return;
    }

    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      setLocalError('Password must contain at least one special character');
      return;
    }

    try {
      setLocalLoading(true);

      // Call createPassword Cloud Function natively via httpsCallable
      const createPasswordFn = httpsCallable(functions, 'createPassword');
      const response = await createPasswordFn({
        userId,
        email,
        password,
      });

      const data = response.data as { success: boolean; error?: string };
      // console.log('Password creation result:', data);

      if (data.success) {
        // console.log('Password created successfully! Logging in...');

        // Sign in the user automatically. Force local persistence so this session
        // survives opening the verification link in a new tab (email clients open
        // links in a new tab, which doesn't share sessionStorage-based sessions).
        await setPersistence(auth, browserLocalPersistence);
        const credential = await signInWithEmailAndPassword(auth, email, password);
        await credential.user.reload();

        // Profile form only after email is verified (preview link or Auth verify link).
        if (!credential.user.emailVerified) {
          navigate('/verify-email', { replace: true });
          return;
        }

        if (userData?.userType === 'investor') {
          navigate('/express-signup', { replace: true });
        } else if (userData?.userType === 'startup') {
          navigate('/profile/startup', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } else {
        setLocalError(data.error || 'Failed to create password');
      }
    } catch (error: unknown) {
      console.error('Password creation error:', error);

      // Handle specific error cases
      if (error && typeof error === 'object' && 'code' in error) {
        const authError = error as { code: string };
        if (authError.code === 'functions/invalid-argument') {
          setLocalError('Invalid email or password format');
        } else if (authError.code === 'functions/not-found') {
          setLocalError('User not found. Please complete the signup process first.');
        } else if (authError.code === 'functions/already-exists') {
          setLocalError('User already has a password set');
        } else {
          setLocalError('Failed to create password. Please try again.');
        }
      } else {
        setLocalError('Failed to create password. Please try again.');
      }
    } finally {
      setLocalLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Show loading state while fetching user data
  if (loadingUserData) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-3 text-white/80">
          <svg
            className="h-6 w-6 animate-spin text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      </div>
    );
  }

  // Show error if no userId provided
  if (!userId) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500">User ID is required</div>
      </div>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      {/* Email Field */}
      <Input
        type="email"
        value={email}
        onChange={e => {
          setEmail(e.target.value);
          setHasTyped(true);
        }}
        required
        placeholder="Enter your email"
        label="Email Address"
      />

      {/* Password Field */}
      <Input
        type="password"
        value={password}
        onChange={e => {
          setPassword(e.target.value);
          setHasTyped(true);
        }}
        required
        placeholder="Enter your password"
        label="Password"
        showPasswordToggle={true}
        showPassword={showPassword}
        onTogglePassword={togglePasswordVisibility}
      />

      {/* Password Requirements */}
      <div className="text-xs text-gray-300 text-left">
        <p className="mb-2">Password requirements:</p>
        <div className="space-y-1">
          <div className="flex items-center">
            {password.length >= 8 ? (
              <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <div className="w-2 h-2 mr-2 bg-gray-400 rounded-full"></div>
            )}
            <span className="text-gray-400">Minimum 8 characters</span>
          </div>
          <div className="flex items-center">
            {/\d/.test(password) ? (
              <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <div className="w-2 h-2 mr-2 bg-gray-400 rounded-full"></div>
            )}
            <span className="text-gray-400">At least one number</span>
          </div>
          <div className="flex items-center">
            {/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password) ? (
              <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <div className="w-2 h-2 mr-2 bg-gray-400 rounded-full"></div>
            )}
            <span className="text-gray-400">At least one special character</span>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {(error || localError) && <Alert type="error">{error || localError}</Alert>}

      {/* Submit Button */}
      <Button type="submit" disabled={loading || localLoading}>
        {loading || localLoading ? (
          <div className="flex items-center justify-center">
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Creating Password...
          </div>
        ) : (
          'Create Password'
        )}
      </Button>

      <p className="mt-4 text-center text-xs text-gray-500">
        Need to change your email?{' '}
        <Link to="/contact" className="text-[#00b0f0] hover:underline">
          Contact us
        </Link>{' '}
        and we&apos;ll fix it.
      </p>
    </Form>
  );
};

export default FinalStep;
